import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { HttpError } from "../api/client";
import { createJob } from "../api/jobs";
import { getLibrary, removeSongFromPlaylist } from "../api/playlists";
import { TERMINAL_STATUSES } from "../api/types";
import type { CreateJobRequest, LibraryResponse } from "../api/types";
import { HomeLibraryTabs } from "../components/HomeLibraryTabs";
import { JobProgress } from "../components/JobProgress";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { PlaylistTabs } from "../components/PlaylistTabs";
import { RecentSongs } from "../components/RecentSongs";
import { UrlForm } from "../components/UrlForm";
import { useJobPolling } from "../hooks/useJobPolling";
import { navigate } from "../hooks/useLocation";
import { useNotifications } from "../notifications/useNotifications";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof HttpError) return err.toUserMessage();
  if (err instanceof Error) return err.message;
  return fallback;
}

function removeKey(playlistSlug: string, jobId: string): string {
  return `${playlistSlug}:${jobId}`;
}

export function HomePage() {
  const { t } = useTranslation();
  const { notifyError, notifySuccess } = useNotifications();
  const [jobId, setJobId] = useState<string | null>(null);
  const [library, setLibrary] = useState<LibraryResponse | null>(null);
  const [pendingRemoveKey, setPendingRemoveKey] = useState<string | null>(null);
  const lastPollErrorRef = useRef<string | null>(null);
  const lastJobFailureRef = useRef<string | null>(null);

  const {
    data: status,
    error: pollError,
    isPolling,
  } = useJobPolling(jobId, t("home.errors.pollJob"));

  const loadLibrary = useCallback(async () => {
    try {
      const response = await getLibrary();
      setLibrary(response);
    } catch (err) {
      const message = errorMessage(err, t("home.errors.fetchPlaylists"));
      notifyError(message);
    }
  }, [notifyError, t]);

  // Initial library fetch on mount. The call goes through a ref so that the
  // `react-hooks/set-state-in-effect` rule does not statically trace into
  // `loadLibrary`'s setState calls — those calls are all post-`await`, so they
  // do not cause cascading renders, but the rule cannot tell that from the
  // call site alone.
  const loadLibraryRef = useRef(loadLibrary);
  useEffect(() => {
    loadLibraryRef.current = loadLibrary;
  }, [loadLibrary]);
  useEffect(() => {
    void loadLibraryRef.current();
  }, []);

  const handleSubmit = useCallback(async (payload: CreateJobRequest) => {
    try {
      const response = await createJob(payload);
      setJobId(response.job_id);
    } catch (err) {
      const message = errorMessage(err, t("home.errors.createJob"));
      notifyError(message);
    }
  }, [notifyError, t]);

  const handleRemoveFromPlaylist = useCallback(
    async (playlistSlug: string, songJobId: string) => {
      const key = removeKey(playlistSlug, songJobId);
      const playlistName =
        library?.playlists.find((playlist) => playlist.slug === playlistSlug)?.name ??
        playlistSlug;
      setPendingRemoveKey(key);
      try {
        await removeSongFromPlaylist(playlistSlug, songJobId);
        await loadLibrary();
        notifySuccess(
          t("notifications.removedFromPlaylist", { playlist: playlistName }),
        );
      } catch (err) {
        const message = errorMessage(err, t("home.errors.removeSong"));
        notifyError(message);
      } finally {
        setPendingRemoveKey(null);
      }
    },
    [library, loadLibrary, notifyError, notifySuccess, t],
  );

  // Once the job reaches a terminal status, refresh the library so the new
  // song surfaces in `Recent` (only for `completed`) and then clear `jobId`
  // so the URL form re-appears and the progress card unmounts. The
  // `setJobId(null)` is intentionally placed after the library refresh —
  // clearing `jobId` synchronously would cancel the polling and discard the
  // refresh queue before `Recent` has the new entry.
  useEffect(() => {
    if (!jobId || !status || !TERMINAL_STATUSES.has(status.status)) return;
    let cancelled = false;
    void (async () => {
      if (status.status === "completed") {
        await loadLibrary();
      }
      if (cancelled) return;
      setJobId(null);
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, loadLibrary, status]);

  useEffect(() => {
    if (!pollError) {
      lastPollErrorRef.current = null;
      return;
    }
    if (lastPollErrorRef.current === pollError) return;
    lastPollErrorRef.current = pollError;
    notifyError(pollError);
  }, [notifyError, pollError]);

  useEffect(() => {
    const message = status?.status === "failed" ? status.error : null;
    if (!message) {
      if (status?.status !== "failed") {
        lastJobFailureRef.current = null;
      }
      return;
    }
    const key = `${jobId ?? ""}:${message}`;
    if (lastJobFailureRef.current === key) return;
    lastJobFailureRef.current = key;
    notifyError(message);
  }, [jobId, notifyError, status?.error, status?.status]);

  const isWorking = isPolling && !(status && TERMINAL_STATUSES.has(status.status));
  const playlists = library?.playlists ?? [];
  const recentSongs = library?.recent_songs ?? [];

  function handleReset() {
    setJobId(null);
    if (window.location.pathname !== "/") {
      navigate("/");
    }
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-5xl flex-col gap-8 px-4 py-10">
      <header className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={handleReset}
          className="group flex min-w-0 flex-1 items-center gap-3 text-left text-3xl font-semibold tracking-tight text-stone-100 transition-colors hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-950 sm:text-4xl"
          aria-label={t("app.homeAriaLabel")}
        >
          <img
            src="/logo.svg"
            alt=""
            className="h-12 w-12 flex-none rounded-xl border border-stone-700 bg-stone-900 object-contain p-1.5 shadow-lg shadow-black/30 transition-colors group-hover:border-stone-500 sm:h-14 sm:w-14"
          />
          <span className="min-w-0 truncate">Bilgili Karaoke</span>
        </button>
        <div className="shrink-0">
          <LanguageSwitcher />
        </div>
      </header>

      {!jobId && (
        <UrlForm disabled={isWorking} onSubmit={(payload) => void handleSubmit(payload)} />
      )}

      {jobId && (
        <section className="space-y-6 rounded-2xl border border-stone-800 bg-stone-900/60 p-6 shadow-xl">
          <JobProgress status={status} isPolling={isWorking} />
        </section>
      )}

      <HomeLibraryTabs
        playlistsSlot={
          <PlaylistTabs
            playlists={playlists}
            pendingRemoveKey={pendingRemoveKey}
            onRemoveFromPlaylist={handleRemoveFromPlaylist}
          />
        }
        recentSlot={<RecentSongs songs={recentSongs} />}
      />

      <footer className="text-center text-xs text-stone-500">
        {t("footer.rights")}
      </footer>
    </main>
  );
}
